import { Injectable } from '@angular/core';
import { ArticleUsageVO, ArticleVO } from '@article/model/vo/article.vo';
import { CaeProjectImportUtils } from '@project/cae/util/cae-project-import-utils';
import { ArticleUsageInfoPropertyDef } from '@article/constants/article-usage-info-property-def';
import { ArticleUsageUtils } from '@article/util/article-usage-utils';
import { ContainerService } from '@3d-scene/model3d/container.service';
import { PropertyValueVO } from '@project/components/article/model/vo/property.vo';
import { PropertyUtils } from '@article/util/property-utils';
import { ArrayUtils } from '@shared/util/array-utils';
import { ProjectModel } from '@project/model/project.model';
import { ArticleContainer } from '@3d-scene/model3d/article-container';
import { CaeItem, CaeRail } from '@project/cae/cae-data.service';
import { ArticleUtils } from '@article/util/article-utils';
import { CaeProjectHelper } from '@project/processor/cae/cae-project-helper';
import { StringUtils } from '@shared/util/string-utils';
import { NumberUtils } from '@shared/util/number-utils';
import { UnitConverter } from '@3d-scene/common/util/unit-converter';
import { MountingRailService, RailArticle, RailGroup } from '@article/rail/mounting-rail.service';
import { ValidatorFn, Validators } from '@angular/forms';
import { MountingRailArticlePostParseProcessor } from '@project/components/article/processor/postparse/mounting-rail-post-parse.processor';
import { CustomValidators } from '@shared/custom-validators';
import { ArticleManager } from '@article/manager/article.manager';
import { lastValueFrom } from 'rxjs';
import { FavoriteArticleVO } from '@user/components/favorites/models/favorite-article.vo';

interface CaeLevel {
  name: string;
  number: number;
}

@Injectable({
  providedIn: 'root'
})
export class CaeImportHelperService {

  constructor(
    private containerService: ContainerService,
    private projectModel: ProjectModel,
    private caeProjectHelper: CaeProjectHelper,
    private articleManager: ArticleManager,
    private mountingRailService: MountingRailService,
  ) { }

  public replaceArticleGuid(articleUsage: ArticleUsageVO, toGuid: string): string {
    let articleUsageId: string;
    if (ArticleUsageUtils.isCompositePartArticleUsage(articleUsage)) {
      this.replaceArticleGuidForCompositeArticle(articleUsage, toGuid);
      articleUsageId = articleUsage.id;
    } else {
      this.replaceArticleGuidForArticle(articleUsage, toGuid);
      articleUsageId = toGuid;
    }
    return articleUsageId;
  }

  private replaceArticleGuidForCompositeArticle(compositePartUsage: ArticleUsageVO, guidToReplace: string): void {
    const compositePartUsages: ArticleUsageVO[] = CaeProjectImportUtils.getAllCompositePartUsages(
      compositePartUsage,
      this.containerService,
    );
    for (const partUsage of compositePartUsages) {
      // for composite articles we should update only first property value
      const values: PropertyValueVO[] = PropertyUtils.getPropertyValuesByPropertyName(
        partUsage.infoProperties,
        ArticleUsageInfoPropertyDef.COMPOSITE_ARTICLE_USAGE_ID,
      );
      if (values && values.length > 0) {
        const value: PropertyValueVO = values[0];
        value.stringValue = guidToReplace;
      } else {
        ArticleUsageUtils.setArticleUsageInfoPropertyStringValue(partUsage, ArticleUsageInfoPropertyDef.COMPOSITE_ARTICLE_USAGE_ID, guidToReplace);
      }
    }
  }

  private replaceArticleGuidForArticle(articleUsage: ArticleUsageVO, toGuid: string): void {
    const fromGuid: string = articleUsage.id;
    const connectedUsageIds: string[] = ArticleUsageUtils.getBackwardConnectedArticleUsageIds(articleUsage);
    ArrayUtils.mergeArrays(connectedUsageIds, ArticleUsageUtils.getAcceptingAreaIds(articleUsage));
    const connectedUsages: ArticleUsageVO[] = [];

    for (const connectedUsageId of connectedUsageIds) {
      connectedUsages.push(this.projectModel.getArticleUsage(connectedUsageId));
    }

    const articleContainer = this.containerService.findArticleContainerInGlobal(fromGuid);
    if (articleContainer) {
      articleContainer.articleGUID = toGuid;
    }

    ArticleUsageUtils.replaceArticleUsageId([ articleUsage ], fromGuid, toGuid);
    ArticleUsageUtils.replaceArticleUsageId(connectedUsages, fromGuid, toGuid);

    this.projectModel.updateArticleUsageCacheKey(fromGuid, toGuid);
  }

  public getArticleContainersForProjectArticle(item: CaeItem): ArticleContainer[] {
    let onRailArticleContainers: ArticleContainer[] = [];

    const article: ArticleVO | undefined = this.caeProjectHelper.getArticleByCaeBaseArticle(item);
    if (article && ArticleUtils.isCompositeArticle(article)) {
      onRailArticleContainers = this.collectAllPartsOfCompositeArticleByGuid(item.id);
    } else {
      const articleContainer: ArticleContainer = this.containerService.findArticleContainerInGlobal(item.id);
      if (articleContainer) {
        onRailArticleContainers.push(articleContainer);
      }
    }

    return onRailArticleContainers;
  }

  private collectAllPartsOfCompositeArticleByGuid(guid: string): ArticleContainer[] {
    const partArticleContainers: ArticleContainer[] = [];
    for (const articleUsage of this.projectModel.getAllArticleUsages()) {
      const compositeUsageId: string = ArticleUsageUtils.getCompositeArticleUsageIdFromUsage(articleUsage);
      if (!StringUtils.isEmpty(compositeUsageId)) {
        if (compositeUsageId === guid) {
          partArticleContainers.push(this.containerService.findArticleContainerInGlobal(articleUsage.id));
        }
      }
    }
    return partArticleContainers;
  }

  public writeArticleNoWithPrefixIntoUsage(articleUsage: ArticleUsageVO, caeArticleNo: string): void {
    if (caeArticleNo && CaeProjectImportUtils.hasWagoArticleNumberPrefix(caeArticleNo)) {
      this.writeArticleInfoIntoUsage(articleUsage, caeArticleNo, ArticleUsageInfoPropertyDef.ARTICLE_NUMBER_WITH_PREFIX);
    }
  }

  public writeArticleInfoIntoUsage(articleUsage: ArticleUsageVO, info: string, property: string): void {
    const usagesToWrite: ArticleUsageVO[] = ArticleUsageUtils.isCompositePartArticleUsage(articleUsage)
      ? CaeProjectImportUtils.getAllCompositePartUsages(articleUsage, this.containerService)
      : [ articleUsage ];
    for (const usage of usagesToWrite) {
      ArticleUsageUtils.setArticleUsageInfoPropertyStringValue(usage, property, info);
    }
  }

  public equalizeItemLevels(items: CaeItem[]): void {
    const levelRange = this.findLevelRangeInItems(items);
    if (!levelRange) {
      // If none of the items has a level, there is nothing to do.
      return;
    }

    const [ minLevel, maxLevel ] = levelRange;
    for (const item of items) {
      this.fillInMissingLevels(item, minLevel, maxLevel);
    }
  }

  /** For a given set of connected labeling articles, find minimum/maximum level number used (->"levels")*/
  private findLevelRangeInItems(items: CaeItem[]): [ min: number, max: number ] | undefined {
    const validLevelNumbers: number[] = items
      .flatMap(item => item.levels)
      .map(level => level?.number)
      .filter((number): number is number => NumberUtils.isNumber(number));

    if (validLevelNumbers.length === 0) {
      return undefined;
    }

    return [Math.min(...validLevelNumbers), Math.max(...validLevelNumbers)];
  }

  /** Add missing rows (item.levels) to a markingstrip cell
   * for example if only the rows (levels) with number 2 and 3 are defined in a cell and minLevel/maxLevel are 1/4,
   * we fill the rest of the rows (1 and 4) with empty entries */
  private fillInMissingLevels(item: CaeItem, minLevel: number, maxLevel: number) {
    const newLevels: CaeLevel[] = [];
    for (let level = minLevel; level <= maxLevel; level++) {
      const levelItem: CaeLevel = item.levels.find(l => l.number == level);
      if (levelItem) {
        newLevels.push(levelItem);
      } else {
        const emptyLevel: CaeLevel = {
          name: '',
          number: level,
        };
        newLevels.push(emptyLevel);
      }
    }
    item.levels = newLevels;
  }

  public millimetersToAway3DUnits(value: number, roundConvertedValue: boolean = true): number {
    if (NumberUtils.isNotNumber(value)) {
      return NaN;
    }
    return UnitConverter.fromToRounded('mm', '3d', value, roundConvertedValue ? 2 : -1);
  }

  public createRaillengthValidator(selectedArticle: RailArticle | null | undefined): ValidatorFn | null {
    let maxMountingRailLength = UnitConverter.fromTo('3d', 'mm', MountingRailArticlePostParseProcessor.MAX_FREEMOUTNING_LENGTH);
    if (selectedArticle) {
      this.articleManager.loadFullArticle(selectedArticle.info.articleId).subscribe(
        (article: ArticleVO | undefined) => {
          maxMountingRailLength = UnitConverter.fromTo('3d', 'mm',
            MountingRailArticlePostParseProcessor.getRailMaxLength(article));
        })
      ;
    }
    return Validators.compose([ CustomValidators.intRequired, CustomValidators.max(maxMountingRailLength) ]);
  }

  public async getPreselectedArticle(caeRails: CaeRail[], articleViewList: RailGroup[]): Promise<RailArticle | undefined> {
    const firstRail = caeRails.at(0);
    if (firstRail?.articleNo) {
      for (const railGroup of articleViewList) {
        for (const railArticle of railGroup.railArticles) {
          if (firstRail.articleNo === railArticle.no || firstRail.articleNo === railArticle.ownNo) {
            return railArticle;
          }
        }
      }
    }

    const favoriteArticles = await lastValueFrom(this.mountingRailService.loadFavoriteMountingRails());
    return this.getFavoriteCarrierrail(articleViewList, favoriteArticles);
  }

  private getFavoriteCarrierrail(articleViewList: RailGroup[], favoriteArticles: FavoriteArticleVO[]): RailArticle | undefined {
    favoriteArticles.sort((a, b) => a.sortOrder - b.sortOrder);
    for (const rail of favoriteArticles) {
      for (const railGroup of articleViewList) {
        for (const railArticle of railGroup.railArticles) {
          if (rail.articleNumber === railArticle.no) {
            return railArticle;
          }
        }
      }
    }
  }
}
